/**
 * Marketing/funnel event tracking.
 *
 * Events are GET beacons to /t, answered 204 at the edge by the CloudFront
 * router function (infrastructure/WebsiteStage.ts), which logs each one as an
 * `ra-mkt` JSON line beside the ra-store-click lines — same log group, same
 * consumer (the analytics dashboard in rescueally-cloud/analytics). No
 * backend, nothing stored client-side, and a failure here must never affect
 * the page.
 *
 * GET (not sendBeacon) on purpose: sendBeacon is POST-only and the website
 * distribution's default behavior only forwards GET/HEAD; `keepalive` gives
 * the same survives-navigation guarantee for the abandonment beacons.
 */
import { readAcquisitionParams } from '../app/acquisition.js';

const ACQUISITION_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'fbclid',
  'gclid'
] as const;

export interface TrackEventInput {
  event: string;
  detail?: Record<string, string | number | undefined>;
}

export const track = ({ event, detail = {} }: TrackEventInput): void => {
  try {
    const params = new URLSearchParams({ e: event, p: window.location.pathname });
    if (document.referrer) params.set('r', document.referrer);
    const utmSource = window.sessionStorage.getItem('utm/source');
    if (utmSource) params.set('utm', utmSource);
    // Campaign params: the landing URL wins, otherwise the stored capture, so
    // an ad visit to the homepage is attributable even though RUM drops the
    // query string and the homepage has no click of its own.
    const search = new URLSearchParams(window.location.search);
    const captured = readAcquisitionParams() ?? {};
    for (const key of ACQUISITION_KEYS) {
      const value = search.get(key) ?? captured[key];
      if (value) params.set(key, value.slice(0, 254));
    }
    for (const [key, value] of Object.entries(detail)) {
      if (value != null) params.set(key, String(value).slice(0, 254));
    }
    void fetch(`/t?${params}`, { method: 'GET', keepalive: true }).catch(() => undefined);
  } catch {
    // Tracking must never break the page.
  }
};
